import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import CandidateLayout from '../../layouts/CandidateLayout';
import Spinner from '../../components/common/Spinner';
import { getSessionResult } from '../../api/candidate.api';

/* ─── Animated count-up (0 → target with easing) ───────────────────── */
const useCountUp = (target, durationMs = 1800, enabled = true) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!enabled || target == null || isNaN(target)) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(Number(target) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, enabled]);
  return val;
};

/* ─── Confetti (CSS-only, fires once on pass) ──────────────────────── */
const COLORS = ['#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#a78bfa', '#fb7185', '#facc15'];
const Confetti = ({ count = 80 }) => {
  const pieces = Array.from({ length: count }, (_, i) => ({
    left:     `${(i * 53) % 100}%`,
    delay:    `${(i * 0.07) % 3}s`,
    duration: `${3 + ((i * 0.31) % 2.5)}s`,
    color:    COLORS[i % COLORS.length],
    size:     6 + (i % 4) * 2,
    rotate:   (i * 47) % 360,
    shape:    i % 3, // 0=square 1=rect 2=circle
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="absolute top-0 animate-confetti-fall"
          style={{
            left:         p.left,
            width:        `${p.shape === 1 ? p.size * 1.6 : p.size}px`,
            height:       `${p.size}px`,
            background:   p.color,
            borderRadius: p.shape === 2 ? '50%' : '2px',
            transform:    `rotate(${p.rotate}deg)`,
            animationDelay:    p.delay,
            animationDuration: p.duration,
            boxShadow:    `0 0 8px ${p.color}80`,
          }}
        />
      ))}
    </div>
  );
};

/* ─── Constellation for the result hero (wide, low opacity) ──────── */
const ResultConstellation = () => {
  const NODES = [
    { x: 50, y: 60 }, { x: 180, y: 30 }, { x: 320, y: 70 },
    { x: 110, y: 150 }, { x: 250, y: 140 }, { x: 380, y: 160 },
  ];
  const EDGES = [[0,1],[1,2],[0,3],[1,3],[1,4],[2,4],[2,5],[3,4],[4,5]];
  return (
    <svg viewBox="0 0 420 220" className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {EDGES.map(([a, b], i) => {
        const A = NODES[a], B = NODES[b];
        return (
          <line
            key={i}
            x1={A.x} y1={A.y} x2={B.x} y2={B.y}
            stroke="#ffffff"
            strokeWidth="0.6"
            className="animate-pulse-line"
            style={{ animationDelay: `${(i * 0.3) % 3}s` }}
          />
        );
      })}
      {NODES.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r="1.8" fill="#ffffff" opacity="0.8" />
      ))}
    </svg>
  );
};

/* ─── Drifting particles for the hero ────────────────────────────── */
const HeroParticles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => (
      <span
        key={i}
        className="absolute bottom-0 rounded-full bg-white animate-drift-up"
        style={{
          left:              `${(i * 47) % 100}%`,
          width:             `${1 + (i % 2)}px`,
          height:            `${1 + (i % 2)}px`,
          animationDelay:    `${(i * 0.9) % 12}s`,
          animationDuration: `${10 + (i % 4)}s`,
          opacity:           0.45,
        }}
      />
    ))}
  </div>
);

/* ─── Animated stat box (reveal-up + count-up for numbers) ────────── */
const StatBox = ({ label, value, colorClass, delay = 0, countUp = false }) => {
  const isNumeric = countUp && typeof value === 'number';
  const animated = useCountUp(isNumeric ? value : 0, 1200, isNumeric);
  const shown = isNumeric ? animated : value;
  return (
    <div
      className="bg-slate-50 rounded-2xl p-4 text-center border border-slate-200 animate-reveal-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <p className={`text-2xl font-bold ${colorClass} tabular-nums`}>{shown ?? '—'}</p>
      <p className="text-xs text-slate-500 mt-1 font-medium">{label}</p>
    </div>
  );
};

const ExamResult = () => {
  useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) return;
    getSessionResult(sessionId)
      .then((res) => setResult(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Results not available yet.'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const passed = result?.pass_status;
  const targetPct = result ? Number(result.percentage) : 0;
  const animatedPct = useCountUp(targetPct, 2200, !!result);

  if (loading) return <CandidateLayout><Spinner className="py-20" /></CandidateLayout>;

  return (
    <CandidateLayout>
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-card-lg overflow-hidden">
          {/* ─── Cinematic hero banner ────────────────────────────── */}
          <div
            className={`relative overflow-hidden px-8 py-10 text-center text-white ${
              passed
                ? 'bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500'
                : 'bg-gradient-to-br from-rose-600 via-red-500 to-orange-500'
            }`}
          >
            {/* Aurora glow blobs */}
            {passed ? (
              <>
                <div className="absolute -top-16 -left-10 w-56 h-56 rounded-full bg-emerald-300/40 blur-3xl animate-aurora-1 mix-blend-screen pointer-events-none" />
                <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full bg-teal-300/40 blur-3xl animate-aurora-2 mix-blend-screen pointer-events-none" />
                <div className="absolute top-1/3 right-1/3 w-40 h-40 rounded-full bg-cyan-200/30 blur-3xl animate-aurora-3 mix-blend-screen pointer-events-none" />
              </>
            ) : (
              <>
                <div className="absolute -top-16 -left-10 w-56 h-56 rounded-full bg-rose-400/40 blur-3xl animate-aurora-1 mix-blend-screen pointer-events-none" />
                <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full bg-orange-300/30 blur-3xl animate-aurora-2 mix-blend-screen pointer-events-none" />
              </>
            )}

            {/* Constellation network */}
            <div className="absolute inset-0 opacity-50">
              <ResultConstellation />
            </div>

            {/* Drifting particles */}
            <HeroParticles />

            {/* Confetti — only on pass */}
            {passed && <Confetti count={80} />}

            {/* Trophy / icon with pulse rings + sparkles */}
            <div className="relative z-10 inline-block mb-4">
              {/* Pulse rings */}
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`absolute inset-0 rounded-full border-2 ${
                    passed ? 'border-emerald-200/60' : 'border-rose-200/60'
                  } animate-ping-ring`}
                  style={{ animationDelay: `${i * 1.05}s` }}
                />
              ))}
              {/* Sparkles around the icon (pass only) */}
              {passed && (
                <>
                  <span className="absolute -top-1 -left-2 w-2 h-2 rounded-full bg-amber-300 animate-sparkle" style={{ animationDelay: '0.2s' }} />
                  <span className="absolute -top-2 right-0 w-1.5 h-1.5 rounded-full bg-white animate-sparkle" style={{ animationDelay: '0.7s' }} />
                  <span className="absolute -bottom-1 -right-2 w-2 h-2 rounded-full bg-amber-200 animate-sparkle" style={{ animationDelay: '1.1s' }} />
                  <span className="absolute -bottom-2 left-0 w-1.5 h-1.5 rounded-full bg-white animate-sparkle" style={{ animationDelay: '1.5s' }} />
                </>
              )}
              <div className={`relative w-24 h-24 rounded-full flex items-center justify-center ring-4 ring-white/30 animate-float-slow ${
                passed ? 'bg-emerald-500/80 backdrop-blur' : 'bg-red-400/80 backdrop-blur'
              }`}>
                {passed ? (
                  <svg className="w-12 h-12 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 4h14v3a5 5 0 01-4.546 4.98A5.001 5.001 0 0113 16v2h3a1 1 0 110 2H8a1 1 0 110-2h3v-2a5.001 5.001 0 01-1.454-4.02A5 5 0 015 7V4zm2 2v1a3 3 0 002.04 2.84A5.018 5.018 0 019 9V6H7zm10 0v3c0 .29-.025.575-.073.852A3 3 0 0017 7V6h-2v3a3 3 0 003 3z" />
                  </svg>
                ) : (
                  <svg className="w-12 h-12 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )}
              </div>
            </div>

            {/* Heading */}
            <h1 className="relative z-10 text-3xl font-bold tracking-tight animate-reveal-up" style={{ animationDelay: '200ms' }}>
              {passed ? (
                <>
                  <span className="bg-gradient-to-r from-amber-100 via-white to-amber-100 bg-clip-text text-transparent">
                    Congratulations!
                  </span>
                  <span className="inline-block ml-2 animate-float-slow">🎉</span>
                </>
              ) : (
                'Better Luck Next Time'
              )}
            </h1>
            <p className="relative z-10 text-white/85 mt-2 text-sm animate-reveal-up" style={{ animationDelay: '350ms' }}>
              {passed ? 'You have successfully passed this assessment.' : 'You did not meet the passing criteria.'}
            </p>
          </div>

          {error && (
            <div className="px-8 py-8 text-center">
              <p className="text-slate-500">{error}</p>
              <p className="text-sm text-slate-400 mt-2">Results may not be available yet or are not enabled for this exam.</p>
            </div>
          )}

          {result && (
            <div className="px-8 py-6">
              {/* Big animated score */}
              <div className="text-center mb-6 py-4 border-b border-slate-100 animate-reveal-up" style={{ animationDelay: '500ms' }}>
                <p className="text-6xl font-bold text-slate-900 tabular-nums tracking-tight leading-none">
                  {animatedPct}<span className="text-3xl text-slate-400 font-semibold">%</span>
                </p>
                <p className="text-slate-500 mt-2 text-sm">{result.score} / {result.total_marks} marks</p>

                {/* Animated score bar with shimmer */}
                <div className="mt-4 bg-slate-100 rounded-full h-2.5 overflow-hidden relative">
                  <div
                    className={`h-full rounded-full transition-all duration-[2200ms] ease-out ${
                      passed
                        ? 'bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500'
                        : 'bg-gradient-to-r from-red-500 via-rose-400 to-red-500'
                    }`}
                    style={{ width: `${Math.min(animatedPct, 100)}%` }}
                  />
                </div>
              </div>

              {/* Stats grid — staggered reveal */}
              <div className="grid grid-cols-2 gap-3">
                <StatBox label="Total Questions" value={Number(result.total_questions)} colorClass="text-slate-700"   delay={600} countUp />
                <StatBox label="Attempted"       value={Number(result.attempted)}       colorClass="text-blue-600"    delay={700} countUp />
                <StatBox label="Correct"         value={Number(result.correct)}         colorClass="text-emerald-600" delay={800} countUp />
                <StatBox label="Wrong"           value={Number(result.wrong)}           colorClass="text-red-500"     delay={900} countUp />
                <StatBox label="Unanswered"      value={Number(result.unanswered)}      colorClass="text-slate-400"   delay={1000} countUp />
                <StatBox
                  label="Result"
                  value={passed ? 'PASS' : 'FAIL'}
                  colorClass={passed ? 'text-emerald-600' : 'text-red-500'}
                  delay={1100}
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-8 pb-6 pt-2 text-center animate-reveal-up" style={{ animationDelay: '1200ms' }}>
            <Link
              to="/candidate/exams"
              className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-sm font-medium hover:underline"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Exams
            </Link>
          </div>
        </div>
      </div>
    </CandidateLayout>
  );
};

export default ExamResult;
